// HAND-BUILT REALISTIC HULLS for the two most-parked rides (2026-11-05, owner:
// "continue … the 3D model replacement"). The SCRAP KART tank (`kart`) and the
// HIND GUNSHIP (`heli`) each sit on 13 maps, and both were a handful of
// rounded slabs. They are now built here, procedurally, from our own
// geometry — no downloaded mesh, so no third-party credit:
//   tank    — a real track BELT (hollow extruded loop) around 5 rubber-rimmed
//             road wheels + sprocket + idler + return rollers, fenders, side
//             skirts, sloped glacis and nose plate, engine-deck grilles and
//             exhausts, a faceted cast turret with tapered cheeks, mantlet,
//             fume-extractor gun, cupola, hatches, MG, smoke dischargers,
//             bustle rack and antenna;
//   gunship — a lofted streamlined fuselage that tapers into the tail boom,
//             stepped tandem canopies, engine deck with intakes/exhausts, stub
//             wings with rocket pods and rails, chin gun, wheeled gear, swept
//             fin, a 5-blade main rotor and a spinning 3-blade tail rotor.
// THE GAME'S LOOK IS KEPT: the same cfg.color / cfg.accent paint, the same
// rounded-panel family and the same vehicleMaps surfaces. The hull paint is
// still `bodyMat` (passed in), so a driver's colour and custom-drawn SKIN land
// exactly as before via VehicleMesh.setDriver, and the accent parts still
// take the driver's glow colour. No new material kinds are introduced, so no
// new shader variants (bench-warmcov).
//
// Everything here runs once at construction; VehicleMesh.mergeVehicleBody
// collapses the hull, and the caller merges the turret / rotor groups, so the
// draw-call count stays per-material. Visual only: colliders live in shared
// (VEHICLES[].half) and are untouched.
import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { worldProjectUV } from "./mapTextures.js";
import { roundCrossSection } from "./avatar.js";

export interface HullWheelSpot { x: number; y: number; z: number; r: number; w: number; spokes?: number }

export interface HullMats {
  /** driver-tinted / skinned hull paint (VehicleMesh.bodyMat) */
  body: THREE.Material;
  /** driver-glow-tinted accent (VehicleMesh.accentMat) */
  accent: THREE.Material;
  dark: THREE.Material;
  glass?: THREE.Material;
  track?: THREE.Material;
}

export interface HullParts {
  turret?: THREE.Group;
  rotor?: THREE.Group;
  tailRotor?: THREE.Group;
}

type V3 = [number, number, number];

function rbox(w: number, h: number, d: number, mat: THREE.Material, p: V3, tile = 0.9): THREE.Mesh {
  // thin trim (rails, blades, grilles) gets one bevel segment: its chamfer is
  // sub-centimetre, so the second segment was pure triangle cost
  const geo = new RoundedBoxGeometry(w, h, d, Math.min(w, h, d) < 0.2 ? 1 : 2, Math.min(0.08, Math.min(w, h, d) * 0.25));
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

/** A plain sharp box for sub-6 cm trim (seams, hinges, ribs, slats): a bevel
 *  that small never resolves on screen, and a rounded box costs 108-300
 *  triangles against this one's 12. */
function tbox(w: number, h: number, d: number, mat: THREE.Material, p: V3, tile = 0.3): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

function rhull(w: number, h: number, d: number, mat: THREE.Material, p: V3, round = 0.85, tile = 0.9): THREE.Mesh {
  const geo = new RoundedBoxGeometry(w, h, d, 3, Math.min(0.1, Math.min(w, h, d) * 0.25));
  roundCrossSection(geo, w, d, round);
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

function cyl(r1: number, r2: number, h: number, mat: THREE.Material, p: V3, axis: "x" | "y" | "z" = "y", seg = 14, tile = 0.4): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(r1, r2, h, seg);
  if (axis === "z") geo.rotateX(-Math.PI / 2); // +y end → −z (forward)
  else if (axis === "x") geo.rotateZ(-Math.PI / 2);
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.position.set(p[0], p[1], p[2]);
  m.castShadow = true;
  return m;
}

/** A lofted body: superellipse cross-sections [z, halfW, halfH, centreY]
 *  joined into one smooth skin (fuselages that taper into a boom). */
function loft(stations: number[][], mat: THREE.Material, seg = 20, n = 2.6, tile = 1.0): THREE.Mesh {
  const pos: number[] = [];
  const idx: number[] = [];
  const e = 2 / n;
  for (const [z, hw, hh, cy] of stations) {
    for (let i = 0; i < seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const c = Math.cos(a), s = Math.sin(a);
      pos.push(hw * Math.sign(c) * Math.abs(c) ** e, cy + hh * Math.sign(s) * Math.abs(s) ** e, z);
    }
  }
  for (let r = 0; r < stations.length - 1; r++) {
    for (let i = 0; i < seg; i++) {
      const a = r * seg + i, b = r * seg + ((i + 1) % seg);
      const c = a + seg, d = b + seg;
      idx.push(a, b, c, b, d, c); // outward-facing (CCW rings marching +z)
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

// ---------------------------------------------------------------- TANK ----

/** Track BELT side profile (u = world z, v = world y) — outer loop and the
 *  inner hole 0.09 m in, so the road wheels show through the open sides. */
function trackShape(): THREE.Shape {
  const L = 1.82, B = 1.3, T = 0.8, t = 0.09;
  const s = new THREE.Shape();
  s.moveTo(-B, 0);
  s.lineTo(B, 0);
  s.quadraticCurveTo(L, 0, L, 0.4);
  s.quadraticCurveTo(L, T - 0.02, L - 0.36, T);
  s.lineTo(-L + 0.32, T);
  s.quadraticCurveTo(-L - 0.02, T, -L, 0.5);
  s.quadraticCurveTo(-L + 0.04, 0.05, -B, 0);
  const h = new THREE.Path();
  h.moveTo(-B + 0.02, t);
  h.quadraticCurveTo(-L + 0.04 + t, 0.05 + t, -L + t, 0.5);
  h.quadraticCurveTo(-L - 0.02 + t, T - t, -L + 0.32, T - t);
  h.lineTo(L - 0.36, T - t);
  h.quadraticCurveTo(L - t, T - 0.02 - t, L - t, 0.4);
  h.quadraticCurveTo(L - t, t, B - 0.02, t);
  h.lineTo(-B + 0.02, t);
  s.holes.push(h);
  return s;
}

function trackBelt(x: number, width: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.ExtrudeGeometry(trackShape(), { depth: width, bevelEnabled: false, curveSegments: 6 });
  geo.rotateY(-Math.PI / 2); // shape x → world z, extrusion → world −x
  geo.translate(x + width / 2, 0, 0);
  worldProjectUV(geo, 0.55);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** Faceted cast turret: plan-view polygon extruded up, cheeks tapered in. */
function turretShell(mat: THREE.Material): THREE.Mesh {
  const plan: [number, number][] = [
    [-0.32, -1.0], [0.32, -1.0], [0.82, -0.5], [0.86, 0.7], [0.62, 1.1], [-0.62, 1.1], [-0.86, 0.7], [-0.82, -0.5],
  ];
  const s = new THREE.Shape();
  plan.forEach(([x, z], i) => (i ? s.lineTo(x, -z) : s.moveTo(x, -z)));
  const H = 0.46;
  const geo = new THREE.ExtrudeGeometry(s, { depth: H, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 2, curveSegments: 1 });
  geo.rotateX(-Math.PI / 2); // extrusion → +y, shape y → −z
  const p = geo.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i);
    const k = 1 - 0.13 * Math.max(0, Math.min(1, y / (H + 0.05)));
    p.setXYZ(i, p.getX(i) * k, y + 0.05, p.getZ(i) * k);
  }
  geo.computeVertexNormals();
  worldProjectUV(geo, 1.0);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** The SCRAP KART tank, in the pre-scale frame the old build used (the
 *  caller scales the group 0.82). Forward −z, ground y 0. */
export function buildTankHull(
  group: THREE.Group, mats: HullMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body: armour, accent, dark } = mats;
  const track = mats.track ?? dark;
  const TX = 1.1, TW = 0.55;
  // TRACK BELTS + running gear
  for (const sx of [-1, 1]) group.add(trackBelt(sx * TX, TW, track));
  const spots: HullWheelSpot[] = [];
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 5; i++) spots.push({ x: sx * TX, y: 0.37, z: -1.1 + i * 0.55, r: 0.28, w: 0.42, spokes: 6 });
    spots.push({ x: sx * TX, y: 0.5, z: -1.5, r: 0.25, w: 0.4, spokes: 8 }); // drive sprocket
    spots.push({ x: sx * TX, y: 0.42, z: 1.48, r: 0.26, w: 0.4, spokes: 6 }); // idler
  }
  wheels(spots);
  for (const sx of [-1, 1]) for (const z of [-0.6, 0, 0.6]) group.add(cyl(0.08, 0.08, 0.36, dark, [sx * TX, 0.63, z], "x", 10)); // return rollers
  // HULL
  group.add(rbox(1.66, 0.55, 3.3, armour, [0, 0.58, 0], 1.0)); // lower hull between the tracks
  group.add(rbox(2.44, 0.1, 3.5, armour, [0, 0.9, 0.04], 1.0)); // fender deck over the tracks
  group.add(rbox(1.92, 0.34, 2.6, armour, [0, 1.1, 0.28], 1.0)); // upper hull / engine deck
  const glacis = rbox(1.92, 0.3, 1.0, armour, [0, 0.98, -1.24], 1.0);
  glacis.rotation.x = -0.32; group.add(glacis); // sloped glacis
  const nose = rbox(1.66, 0.42, 0.26, armour, [0, 0.55, -1.66], 1.0);
  nose.rotation.x = 0.55; group.add(nose); // lower nose plate
  group.add(rbox(1.92, 0.5, 0.16, armour, [0, 0.82, 1.72], 1.0)); // rear plate
  for (const sx of [-1, 1]) {
    group.add(rbox(0.07, 0.22, 3.3, armour, [sx * 1.23, 0.82, 0.02], 0.8)); // side skirt
    group.add(rbox(0.46, 0.12, 0.08, accent, [sx * 0.98, 0.9, -1.78], 0.5)); // fender-tip hazard bar
    group.add(rbox(0.34, 0.2, 0.86, armour, [sx * 0.98, 1.05, 0.72], 0.8)); // fender stowage box
    group.add(rbox(0.16, 0.12, 0.08, dark, [sx * 0.6, 1.02, -1.74], 0.4)); // headlight guard
    group.add(cyl(0.05, 0.05, 0.12, dark, [sx * 0.6, 1.02, -1.8], "z", 10)); // headlight
    group.add(rbox(0.12, 0.12, 0.16, dark, [sx * 0.5, 0.42, -1.82], 0.4)); // tow hook
    group.add(cyl(0.08, 0.09, 0.32, dark, [sx * 0.62, 0.98, 1.86], "z", 12)); // exhaust
  }
  for (let i = 0; i < 5; i++) group.add(rbox(1.36, 0.035, 0.07, dark, [0, 1.285, 0.92 + i * 0.12], 0.4)); // deck grille
  group.add(rbox(1.2, 0.05, 0.22, track, [0, 1.21, -0.9], 0.5)); // spare track links on the glacis edge

  // TURRET on its own yaw pivot (VehicleMesh.setTurretYaw)
  const turret = new THREE.Group();
  turret.position.set(0, 1.26, 0);
  turret.add(turretShell(armour));
  turret.add(rbox(0.62, 0.38, 0.32, armour, [0, 0.27, -1.1], 0.6)); // mantlet
  turret.add(cyl(0.085, 0.1, 2.1, dark, [0, 0.27, -2.2], "z", 14)); // gun tube
  turret.add(cyl(0.14, 0.14, 0.42, dark, [0, 0.27, -2.3], "z", 14)); // fume extractor
  turret.add(cyl(0.12, 0.12, 0.18, dark, [0, 0.27, -3.22], "z", 14)); // muzzle
  turret.add(cyl(0.26, 0.29, 0.2, armour, [0.36, 0.6, 0.42], "y", 14)); // commander's cupola
  turret.add(cyl(0.22, 0.22, 0.05, dark, [0.36, 0.72, 0.42], "y", 14)); // cupola hatch
  for (const a of [-0.9, 0, 0.9]) turret.add(rbox(0.1, 0.07, 0.06, dark, [0.36 + Math.sin(a) * 0.27, 0.62, 0.42 - Math.cos(a) * 0.27], 0.3)); // vision blocks
  turret.add(cyl(0.22, 0.22, 0.05, armour, [-0.36, 0.54, 0.32], "y", 14)); // loader's hatch
  turret.add(rbox(0.08, 0.16, 0.08, dark, [-0.36, 0.64, -0.12], 0.3)); // MG mount
  turret.add(cyl(0.03, 0.03, 0.8, dark, [-0.36, 0.72, -0.4], "z", 8)); // MG barrel
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 3; i++) turret.add(cyl(0.05, 0.05, 0.22, dark, [sx * (0.62 + i * 0.07), 0.38 + i * 0.03, -0.66], "z", 8)); // smoke dischargers
  }
  turret.add(cyl(0.015, 0.025, 1.3, dark, [0.58, 1.1, 0.98], "y", 6)); // whip antenna
  for (const y of [0.22, 0.42]) turret.add(rbox(1.3, 0.05, 0.05, dark, [0, y, 1.3], 0.4)); // bustle rack rails
  for (const x of [-0.6, 0, 0.6]) turret.add(rbox(0.05, 0.3, 0.05, dark, [x, 0.32, 1.3], 0.4)); // rack posts
  group.add(turret);
  return { turret };
}

// ------------------------------------------------------------- GUNSHIP ----

/** The HIND GUNSHIP. Forward −z, ground y 0 (wheels). */
export function buildGunshipHull(
  group: THREE.Group, mats: HullMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body, accent, dark } = mats;
  const glass = mats.glass ?? dark;
  // FUSELAGE: one lofted skin from the nose through the cabin into the boom
  group.add(loft([
    [-2.6, 0.04, 0.04, 0.95],
    [-2.5, 0.28, 0.3, 0.98],
    [-2.2, 0.45, 0.48, 1.05],
    [-1.6, 0.62, 0.62, 1.12],
    [-0.8, 0.78, 0.72, 1.2],
    [0.2, 0.86, 0.75, 1.25],
    [1.1, 0.8, 0.68, 1.3],
    [1.7, 0.52, 0.48, 1.45],
    [2.4, 0.3, 0.3, 1.66],
    [3.6, 0.2, 0.2, 1.85],
    [4.25, 0.17, 0.17, 1.92],
    [4.4, 0.03, 0.03, 1.93],
  ], body));
  // stepped tandem canopies (gunner low in front, pilot raised behind)
  group.add(rhull(0.7, 0.44, 0.95, glass, [0, 1.6, -1.72], 1, 0.9));
  group.add(rhull(0.82, 0.56, 1.05, glass, [0, 1.92, -0.72], 1, 0.9));
  group.add(rbox(0.74, 0.07, 0.07, dark, [0, 1.84, -1.24], 0.3)); // canopy frame
  group.add(rbox(0.07, 0.07, 1.0, dark, [0, 2.2, -0.72], 0.3));   // canopy spine
  // ENGINE DECK + intakes + exhausts
  group.add(rhull(1.3, 0.5, 2.4, body, [0, 1.98, 0.35], 0.8, 1.0));
  for (const sx of [-1, 1]) {
    group.add(cyl(0.2, 0.22, 0.3, dark, [sx * 0.42, 2.0, -0.9], "z", 14)); // intake
    group.add(cyl(0.16, 0.2, 0.5, dark, [sx * 0.6, 2.02, 1.5], "z", 12));  // exhaust
  }
  group.add(cyl(0.12, 0.16, 0.5, dark, [0, 2.45, 0.2], "y", 12)); // mast
  group.add(cyl(0.28, 0.32, 0.18, accent, [0, 2.72, 0.2], "y", 14)); // rotor head
  // STUB WINGS with rocket pods + wingtip rails
  for (const sx of [-1, 1]) {
    const wing = rbox(1.5, 0.09, 0.75, body, [sx * 1.45, 1.3, 0.25], 0.9);
    wing.rotation.z = -sx * 0.12; group.add(wing); // anhedral
    for (const ax of [1.15, 1.8]) {
      const wy = 1.3 - (ax - 1.45) * 0.12;
      group.add(rbox(0.06, 0.16, 0.4, dark, [sx * ax, wy - 0.12, 0.25], 0.3)); // pylon
      group.add(cyl(0.17, 0.17, 0.95, dark, [sx * ax, wy - 0.34, 0.2], "z", 14)); // rocket pod
      group.add(cyl(0.18, 0.12, 0.12, accent, [sx * ax, wy - 0.34, -0.32], "z", 14)); // pod nose ring
    }
    group.add(rbox(0.1, 0.24, 0.9, dark, [sx * 2.22, 1.13, 0.25], 0.3)); // wingtip rail
  }
  // CHIN GUN
  group.add(rhull(0.42, 0.36, 0.42, dark, [0, 0.66, -2.05], 1, 0.4));
  group.add(cyl(0.04, 0.04, 0.7, dark, [0, 0.6, -2.45], "z", 8));
  // WHEELED GEAR (nose + two mains) on struts
  wheels([
    { x: 0, y: 0.2, z: -1.9, r: 0.2, w: 0.14, spokes: 5 },
    { x: -0.95, y: 0.26, z: 0.35, r: 0.26, w: 0.2, spokes: 5 },
    { x: 0.95, y: 0.26, z: 0.35, r: 0.26, w: 0.2, spokes: 5 },
  ]);
  group.add(cyl(0.05, 0.05, 0.44, dark, [0, 0.44, -1.9], "y", 8));
  for (const sx of [-1, 1]) {
    const strut = cyl(0.06, 0.06, 0.66, dark, [sx * 0.78, 0.52, 0.35], "y", 8);
    strut.rotation.z = sx * 0.62; group.add(strut);
  }
  // TAIL: swept fin + stabiliser (accent, like the old build)
  const fin = rbox(0.1, 1.1, 0.75, accent, [0, 2.32, 4.08], 0.7);
  fin.rotation.x = 0.35; group.add(fin);
  group.add(rbox(1.3, 0.08, 0.4, accent, [0, 1.9, 3.5], 0.7));

  // MAIN ROTOR: 5 blades about a hub (spins about +y)
  const rotor = new THREE.Group();
  rotor.position.set(0, 2.8, 0.2);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const b = rbox(4.3, 0.06, 0.34, dark, [Math.cos(a) * 2.45, 0, -Math.sin(a) * 2.45], 0.9);
    b.rotation.y = a; rotor.add(b);
  }
  rotor.add(cyl(0.22, 0.22, 0.12, dark, [0, 0.02, 0], "y", 12));
  group.add(rotor);
  // TAIL ROTOR: 3 blades in the YZ plane (spins about +x)
  const tailRotor = new THREE.Group();
  tailRotor.position.set(0.16, 2.45, 4.22);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const b = rbox(0.05, 0.75, 0.12, dark, [0, Math.cos(a) * 0.38, Math.sin(a) * 0.38], 0.4);
    b.rotation.x = a; tailRotor.add(b);
  }
  tailRotor.add(cyl(0.07, 0.07, 0.12, dark, [0, 0, 0], "x", 10));
  group.add(tailRotor);
  return { rotor, tailRotor };
}

// ------------------------------------------------------- LIFT SURFACES ----

/** One spanwise station of a lifting surface: `s` span position, `le` the
 *  leading-edge z, `chord`, thickness ratio `t`, chord-line height `y`, and an
 *  optional `tw` twist (radians, nose-up) about the quarter chord. */
export interface FoilStation { s: number; le: number; chord: number; t: number; y: number; tw?: number }

/** NACA 4-digit half-thickness at chord fraction x (closed trailing edge). */
function foilHalf(x: number, t: number): number {
  return 5 * t * (0.2969 * Math.sqrt(x) - 0.126 * x - 0.3516 * x * x + 0.2843 * x ** 3 - 0.1036 * x ** 4);
}

/** A real AIRFOIL skin lofted through spanwise stations (span = +x, chord
 *  = +z, thickness = +y) with flat caps at both ends. A wing, a tailplane,
 *  a fin (rotate it) or a propeller blade (span = y). The section is a
 *  lightly cambered NACA profile: round leading edge, sharp trailing edge —
 *  the single read that a slab wing never gave. */
export function foil(stations: FoilStation[], mat: THREE.Material, n = 7, camber = 0.02, tile = 0.9): THREE.Mesh {
  const ring: [number, number][] = []; // (x along chord, side +1 top / -1 bottom)
  for (let i = 0; i <= n; i++) ring.push([(1 + Math.cos((Math.PI * i) / n)) / 2, 1]); // TE → LE over the top
  for (let i = 1; i < n; i++) ring.push([(1 - Math.cos((Math.PI * i) / n)) / 2, -1]); // LE → TE underneath
  const M = ring.length;
  const pos: number[] = [];
  const idx: number[] = [];
  const pt = (st: FoilStation, x: number, side: number): [number, number, number] => {
    const yc = camber * (x < 0.4 ? (2 * 0.4 * x - x * x) / 0.16 : ((1 - 0.8) + 0.8 * x - x * x) / 0.36);
    let dy = (yc + side * foilHalf(x, st.t)) * st.chord;
    let dz = (x - 0.25) * st.chord;
    const tw = st.tw ?? 0;
    if (tw) { // nose-up twist about the quarter chord
      const c = Math.cos(tw), s = Math.sin(tw);
      const ny = dy * c - dz * s, nz = dy * s + dz * c;
      dy = ny; dz = nz;
    }
    return [st.s, st.y + dy, st.le + 0.25 * st.chord + dz];
  };
  for (const st of stations) for (const [x, side] of ring) pos.push(...pt(st, x, side));
  for (let r = 0; r < stations.length - 1; r++) {
    for (let i = 0; i < M; i++) {
      const a = r * M + i, b = r * M + ((i + 1) % M), c = a + M, d = b + M;
      idx.push(a, c, b, b, c, d);
    }
  }
  // caps: their own vertices so the tip faces stay flat-shaded
  const capAt = (st: FoilStation, flip: boolean): void => {
    const base = pos.length / 3;
    for (const [x, side] of ring) pos.push(...pt(st, x, side));
    for (let i = 1; i < M - 1; i++) {
      if (flip) idx.push(base, base + i + 1, base + i);
      else idx.push(base, base + i, base + i + 1);
    }
  };
  const dir = Math.sign(stations[stations.length - 1].s - stations[0].s) || 1;
  capAt(stations[0], dir > 0);
  capAt(stations[stations.length - 1], dir < 0);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** Mirror a set of half-span stations (s >= 0, root first) into a full
 *  surface running tip → root → tip, so a wing is ONE closed skin. */
function mirrored(half: FoilStation[]): FoilStation[] {
  const out: FoilStation[] = [];
  for (let i = half.length - 1; i > 0; i--) out.push({ ...half[i], s: -half[i].s });
  for (const st of half) out.push(st);
  return out;
}

// ------------------------------------------------------------ MINIPLANE ----

/** The SPARROW light plane (also the ARROW interceptor). Forward −z, wheels
 *  on the ground at y 0. Same footprint and colours as the old build: white
 *  fuselage (`body`, the driver skin), accent wings and tail (driver glow). */
export function buildPlaneHull(
  group: THREE.Group, mats: HullMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body, accent, dark } = mats;
  const glass = mats.glass ?? dark;
  // FUSELAGE: one lofted skin from the cowl face, fat at the cabin, tapering
  // up into the tail cone (the old build was three capsules end to end)
  group.add(loft([
    [-2.0, 0.05, 0.05, 1.0],
    [-1.98, 0.27, 0.25, 1.0],
    [-1.9, 0.35, 0.33, 1.0],
    [-1.6, 0.4, 0.38, 1.0],
    [-1.0, 0.42, 0.4, 1.01],
    [-0.3, 0.41, 0.4, 1.03],
    [0.4, 0.34, 0.34, 1.07],
    [1.2, 0.22, 0.24, 1.14],
    [1.9, 0.12, 0.14, 1.22],
    [2.1, 0.07, 0.08, 1.25],
    [2.16, 0.02, 0.02, 1.26],
  ], body, 18, 2.3, 0.8));
  // cowl face: dark intake mouth either side of the spinner + cheek inlets
  group.add(tbox(0.5, 0.1, 0.06, dark, [0, 0.82, -2.0], 0.3));
  for (const sx of [-1, 1]) {
    group.add(cyl(0.035, 0.04, 0.2, dark, [sx * 0.3, 0.78, -1.55], "z", 8)); // exhaust stub
    for (let i = 0; i < 3; i++) group.add(tbox(0.03, 0.12, 0.05, dark, [sx * 0.405, 1.1, -1.65 + i * 0.1], 0.2)); // cowl louvres
  }
  // CANOPY: a teardrop bubble with a dark frame hoop, a seat back inside
  group.add(rhull(0.56, 0.46, 1.1, glass, [0, 1.36, -0.62], 1, 0.7));
  {
    const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.27, 0.025, 6, 14, Math.PI), dark);
    hoop.position.set(0, 1.36, -0.5); hoop.castShadow = true;
    worldProjectUV(hoop.geometry, 0.3); group.add(hoop);
  }
  group.add(rbox(0.36, 0.34, 0.08, dark, [0, 1.38, -0.32], 0.3)); // seat back / headrest
  group.add(tbox(0.04, 0.03, 0.9, dark, [0, 1.6, -0.62], 0.3));  // canopy rail
  // MAIN WING: tapered, light dihedral, rounded tips (accent, as before)
  group.add(foil(mirrored([
    { s: 0, le: -0.86, chord: 1.18, t: 0.15, y: 1.1 },
    { s: 0.45, le: -0.86, chord: 1.16, t: 0.15, y: 1.11 },
    { s: 1.6, le: -0.82, chord: 1.0, t: 0.13, y: 1.15 },
    { s: 2.55, le: -0.78, chord: 0.84, t: 0.12, y: 1.19 },
    { s: 2.76, le: -0.74, chord: 0.66, t: 0.11, y: 1.2 },
    { s: 2.82, le: -0.66, chord: 0.42, t: 0.1, y: 1.2 },
  ]), accent, 7, 0.025, 1.0));
  for (const sx of [-1, 1]) {
    // aileron + flap hinge lines (dark trim) and a wing-root fillet
    group.add(tbox(0.95, 0.02, 0.03, dark, [sx * 2.05, 1.205, -0.11], 0.3));
    group.add(tbox(0.9, 0.02, 0.03, dark, [sx * 1.0, 1.175, -0.03], 0.3));
    group.add(rhull(0.16, 0.24, 1.0, body, [sx * 0.38, 1.04, -0.3], 1, 0.5));
    // wing-tip strobe pods
    group.add(rhull(0.08, 0.06, 0.26, dark, [sx * 2.8, 1.2, -0.62], 1, 0.2));
  }
  group.add(cyl(0.012, 0.012, 0.35, dark, [1.6, 1.07, -1.0], "z", 6)); // pitot tube
  // TAIL: tapered tailplane with elevator line, swept fin with rudder line
  group.add(foil(mirrored([
    { s: 0, le: 1.42, chord: 0.66, t: 0.1, y: 1.3 },
    { s: 0.85, le: 1.52, chord: 0.5, t: 0.09, y: 1.3 },
    { s: 1.0, le: 1.58, chord: 0.38, t: 0.08, y: 1.3 },
  ]), accent, 6, 0, 0.8));
  {
    const fin = foil([
      { s: 0, le: 1.3, chord: 0.86, t: 0.1, y: 0 },
      { s: 0.3, le: 1.5, chord: 0.66, t: 0.1, y: 0 },
      { s: 0.78, le: 1.74, chord: 0.44, t: 0.09, y: 0 },
      { s: 0.86, le: 1.82, chord: 0.34, t: 0.08, y: 0 },
    ], accent, 6, 0, 0.8);
    fin.geometry.rotateZ(Math.PI / 2); // span x → +y, thickness → −x
    fin.geometry.translate(0, 1.26, 0);
    worldProjectUV(fin.geometry, 0.8);
    group.add(fin);
  }
  group.add(tbox(0.03, 0.62, 0.03, dark, [0, 1.62, 2.06], 0.2)); // rudder hinge line
  group.add(cyl(0.008, 0.012, 0.42, dark, [0, 1.56, 0.2], "y", 5)); // comm antenna
  // TRICYCLE GEAR: sprung legs into faired wheel spats + a nose leg
  wheels([
    { x: -0.5, y: 0.18, z: -0.4, r: 0.18, w: 0.11, spokes: 0 },
    { x: 0.5, y: 0.18, z: -0.4, r: 0.18, w: 0.11, spokes: 0 },
    { x: 0, y: 0.15, z: -1.5, r: 0.15, w: 0.09, spokes: 0 },
  ]);
  for (const sx of [-1, 1]) {
    const leg = cyl(0.035, 0.03, 0.58, dark, [sx * 0.34, 0.43, -0.4], "y", 8);
    leg.rotation.z = sx * 0.61; group.add(leg);
    group.add(rhull(0.15, 0.24, 0.6, body, [sx * 0.5, 0.26, -0.42], 1, 0.4)); // wheel spat
  }
  group.add(cyl(0.035, 0.035, 0.5, dark, [0, 0.42, -1.5], "y", 8)); // nose leg
  group.add(rhull(0.12, 0.2, 0.46, body, [0, 0.22, -1.5], 1, 0.4));  // nose spat

  // PROPELLER: spinner + two twisted, tapered blades (spins about +z)
  const rotor = new THREE.Group();
  rotor.position.set(0, 1.0, -2.04);
  {
    const sp = new THREE.LatheGeometry([
      new THREE.Vector2(0, -0.3), new THREE.Vector2(0.06, -0.27), new THREE.Vector2(0.12, -0.18),
      new THREE.Vector2(0.16, -0.06), new THREE.Vector2(0.17, 0.04),
    ], 14);
    sp.rotateX(Math.PI / 2); // lathe −y tip → −z: the point leads
    worldProjectUV(sp, 0.3);
    const spinner = new THREE.Mesh(sp, accent);
    spinner.castShadow = true;
    rotor.add(spinner);
  }
  for (const dir of [1, -1]) {
    const blade = foil([
      { s: 0.1, le: -0.07, chord: 0.15, t: 0.18, y: 0, tw: 0.8 },
      { s: 0.35, le: -0.08, chord: 0.17, t: 0.12, y: 0, tw: 1.0 },
      { s: 0.75, le: -0.06, chord: 0.13, t: 0.1, y: 0, tw: 1.2 },
      { s: 0.95, le: -0.03, chord: 0.07, t: 0.1, y: 0, tw: 1.3 },
    ], dark, 5, 0.03, 0.3);
    // span x → ±y; the twist (π/2 − pitch) lays the chord into the disc,
    // coarse at the root, fine at the tip
    blade.geometry.rotateZ(dir * Math.PI / 2);
    blade.geometry.translate(0, 0, -0.05);
    worldProjectUV(blade.geometry, 0.3);
    rotor.add(blade);
  }
  group.add(rotor);
  return { rotor };
}

// --------------------------------------------------------------- TRUCK ----

/** A side PROFILE (points as [z, y], forward −z) extruded across the width,
 *  centred on x = 0, with rounded edges. Cab shells, fairings. */
function profileSolid(pts: [number, number][], width: number, mat: THREE.Material, bevel = 0.07, tile = 1.1): THREE.Mesh {
  const s = new THREE.Shape();
  pts.forEach(([z, y], i) => (i ? s.lineTo(z, y) : s.moveTo(z, y)));
  s.closePath();
  const depth = Math.max(0.01, width - bevel * 2);
  const geo = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel * 0.9, bevelSegments: 2, curveSegments: 1 });
  geo.rotateY(-Math.PI / 2); // shape x → world z, extrusion → world −x
  geo.translate(depth / 2, 0, 0);
  geo.computeVertexNormals();
  worldProjectUV(geo, tile);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** A wheel-arch fender: an annulus sector over an axle, extruded `w` wide. */
function fenderArc(x: number, y: number, z: number, r: number, w: number, mat: THREE.Material): THREE.Mesh {
  const t = 0.07, a0 = 0.12, a1 = Math.PI - 0.12;
  const s = new THREE.Shape();
  s.absarc(0, 0, r + t, a0, a1, false);
  s.absarc(0, 0, r, a1, a0, true);
  s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: w, bevelEnabled: false, curveSegments: 10 });
  geo.rotateY(-Math.PI / 2);
  geo.translate(x + w / 2, y, z);
  geo.computeVertexNormals();
  worldProjectUV(geo, 0.5);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

export interface TruckMats extends HullMats {
  /** riveted cargo-box plate (accent hue) */
  box: THREE.Material;
  /** chrome: tanks, stacks, bull bar, rims */
  chrome: THREE.Material;
  /** red tail / marker lens */
  lens: THREE.Material;
}

/** COLOSSUS road train (cab-over tractor + rigid box). Forward −z, ground
 *  y 0. Same layout as the old build: body-paint cab, accent cargo box,
 *  four axles at the same stations, the same 1.45 m track. */
export function buildTruckHull(
  group: THREE.Group, mats: TruckMats, wheels: (spots: HullWheelSpot[]) => void,
): HullParts {
  const { body, accent, dark, box: boxPlate, chrome, lens } = mats;
  const glass = mats.glass ?? dark;
  // CAB: one extruded side profile — near-vertical face, raked windshield,
  // flat roof — rounded on every edge
  group.add(profileSolid([
    [-3.36, 0.95], [-1.28, 0.95], [-1.28, 2.98], [-3.0, 2.98], [-3.2, 2.88], [-3.42, 1.95], [-3.42, 1.1],
  ], 2.56, body, 0.09, 1.2));
  // roof AIR FAIRING: a ramp up from the windshield header (body), accent cap
  group.add(profileSolid([
    [-2.92, 2.96], [-1.32, 2.96], [-1.32, 3.46], [-2.0, 3.48], [-2.6, 3.25],
  ], 2.38, body, 0.06, 1.2));
  group.add(rbox(2.3, 0.08, 0.7, accent, [0, 3.5, -1.66], 1.0));
  for (let i = 0; i < 5; i++) group.add(tbox(0.12, 0.06, 0.08, accent, [-0.5 + i * 0.25, 3.05, -3.08], 0.2)); // roof marker lights
  // WINDSHIELD: two raked panes split by a centre post, sun visor above
  for (const sx of [-1, 1]) {
    const pane = tbox(1.06, 0.84, 0.05, glass, [sx * 0.55, 2.42, -3.35], 1.1);
    pane.rotation.x = 0.248; group.add(pane);
  }
  { const post = tbox(0.06, 0.9, 0.07, dark, [0, 2.42, -3.36], 0.3); post.rotation.x = 0.248; group.add(post); }
  { const visor = tbox(2.34, 0.06, 0.34, body, [0, 2.98, -3.28], 0.6); visor.rotation.x = -0.12; group.add(visor); }
  for (let i = 0; i < 2; i++) group.add(tbox(0.08, 0.03, 0.3, dark, [-0.3 + i * 0.6, 2.95, -3.32], 0.2)); // wiper parks
  // GRILLE: dark recess, chrome slats, a badge; headlights in chrome bezels
  group.add(tbox(1.7, 0.66, 0.06, dark, [0, 1.52, -3.44], 0.35));
  for (let i = 0; i < 5; i++) group.add(tbox(1.62, 0.04, 0.05, chrome, [0, 1.27 + i * 0.12, -3.47], 0.3));
  group.add(tbox(0.36, 0.1, 0.03, accent, [0, 1.9, -3.45], 0.2)); // badge
  for (const sx of [-1, 1]) {
    group.add(tbox(0.44, 0.24, 0.06, chrome, [sx * 1.04, 1.18, -3.44], 0.2)); // headlight bezel
    group.add(tbox(0.36, 0.17, 0.04, glass, [sx * 1.04, 1.18, -3.48], 0.2));  // headlight lens
    group.add(tbox(0.14, 0.1, 0.05, accent, [sx * 1.04, 1.38, -3.44], 0.2)); // indicator
  }
  // BUMPER + tubular bull bar + tow hooks
  group.add(rbox(2.66, 0.36, 0.34, dark, [0, 0.82, -3.52], 0.5));
  group.add(cyl(0.055, 0.055, 2.2, chrome, [0, 1.22, -3.74], "x", 10));
  group.add(cyl(0.055, 0.055, 1.7, chrome, [0, 0.96, -3.78], "x", 10));
  for (const sx of [-0.62, 0.62]) {
    group.add(cyl(0.06, 0.06, 0.62, chrome, [sx, 1.0, -3.74], "y", 10));
    group.add(rbox(0.1, 0.12, 0.2, dark, [sx * 0.8, 0.6, -3.66], 0.2)); // tow hook
  }
  // DOORS: glass, seam lines, handles, grab rails, two steps; mirrors
  for (const sx of [-1, 1]) {
    const x = sx * 1.28;
    group.add(tbox(0.05, 0.62, 1.08, glass, [x, 2.42, -2.42], 1.1));     // door glass
    group.add(tbox(0.04, 0.05, 1.12, dark, [x, 2.08, -2.42], 0.2));      // window sill
    group.add(tbox(0.035, 1.85, 0.03, dark, [x, 1.95, -1.82], 0.2));     // rear door seam
    group.add(tbox(0.04, 0.06, 0.18, chrome, [x + sx * 0.02, 1.9, -2.0], 0.2)); // handle
    group.add(cyl(0.025, 0.025, 1.2, chrome, [x + sx * 0.05, 1.85, -1.48], "y", 6)); // grab rail
    group.add(tbox(0.3, 0.05, 0.42, dark, [sx * 1.16, 0.78, -1.62], 0.3)); // step
    group.add(tbox(0.3, 0.05, 0.42, dark, [sx * 1.16, 0.42, -1.62], 0.3)); // step
    group.add(cyl(0.03, 0.03, 0.42, dark, [sx * 1.46, 2.7, -3.12], "x", 6)); // mirror arm
    group.add(cyl(0.025, 0.025, 0.7, dark, [sx * 1.66, 2.42, -3.12], "y", 6)); // mirror stalk
    group.add(rbox(0.08, 0.5, 0.26, dark, [sx * 1.68, 2.42, -3.18], 0.2));     // mirror head
    group.add(tbox(0.02, 0.44, 0.2, glass, [sx * 1.68, 2.42, -3.31], 0.2));    // mirror glass
    // air horns on the roof
    group.add(cyl(0.05, 0.08, 0.5, chrome, [sx * 0.5, 3.06, -2.6], "z", 8));
  }
  // CARGO BOX: riveted plate body, ribbed sides, rails, corner posts
  group.add(rbox(2.8, 2.4, 4.6, boxPlate, [0, 1.9, 0.9], 1.1));
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 8; i++) group.add(tbox(0.05, 2.2, 0.07, boxPlate, [sx * 1.41, 1.9, -1.1 + i * 0.57], 0.5)); // side ribs
    group.add(rbox(0.08, 0.14, 4.62, dark, [sx * 1.4, 0.74, 0.9], 0.4)); // bottom rail
    for (const z of [-1.4, 3.2]) group.add(rbox(0.1, 2.44, 0.1, chrome, [sx * 1.39, 1.9, z], 0.3)); // corner post
    group.add(tbox(1.2, 0.12, 0.05, dark, [sx * 0.7, 3.0, 3.21], 0.2)); // header
  }
  group.add(rbox(2.86, 0.14, 4.66, dark, [0, 3.16, 0.9], 0.6)); // roof cap rail
  // rear doors: centre seam, hinges, chrome lock rods + handles, lights
  group.add(tbox(0.03, 2.2, 0.03, dark, [0, 1.9, 3.215], 0.2));
  for (const sx of [-1, 1]) {
    for (const y of [1.0, 1.65, 2.3, 2.9]) group.add(tbox(0.14, 0.08, 0.05, dark, [sx * 1.3, y, 3.22], 0.2)); // hinges
    for (const x of [0.24, 0.9]) {
      group.add(cyl(0.025, 0.025, 2.2, chrome, [sx * x, 1.9, 3.24], "y", 6)); // lock rod
      group.add(tbox(0.05, 0.22, 0.05, chrome, [sx * (x + 0.06), 1.4, 3.26], 0.2)); // handle
    }
    group.add(tbox(0.22, 0.12, 0.05, lens, [sx * 1.15, 0.58, 3.66], 0.2)); // tail light
    group.add(tbox(0.12, 0.12, 0.05, glass, [sx * 0.85, 0.58, 3.66], 0.2)); // reverse light
    for (const z of [0.0, 1.6]) group.add(tbox(0.04, 0.08, 0.1, lens, [sx * 1.43, 0.86, z], 0.2)); // side markers
  }
  // CHASSIS: two ladder rails + crossmembers (was one 2.9 m slab)
  for (const sx of [-1, 1]) group.add(rbox(0.22, 0.32, 7.3, dark, [sx * 0.55, 0.62, 0], 0.6));
  for (const z of [-3.0, -1.2, 0.6, 2.3, 3.5]) group.add(rbox(1.1, 0.16, 0.14, dark, [0, 0.6, z], 0.4));
  group.add(rbox(2.6, 0.18, 0.2, dark, [0, 0.55, 3.62], 0.4)); // rear underride bar
  group.add(tbox(2.4, 0.06, 0.04, accent, [0, 0.55, 3.73], 0.2)); // reflective strip
  // FUEL TANKS (chrome, strapped) ahead of axle 2; tool box behind it
  for (const sx of [-1, 1]) {
    group.add(cyl(0.3, 0.3, 0.92, chrome, [sx * 1.15, 0.86, -1.55], "z", 16));
    for (const z of [-1.82, -1.28]) group.add(cyl(0.31, 0.31, 0.06, dark, [sx * 1.15, 0.86, z], "z", 16));
    group.add(rbox(0.5, 0.42, 0.62, dark, [sx * 1.12, 0.92, 0.6], 0.4)); // tool box / batteries
    group.add(tbox(0.06, 0.08, 1.1, dark, [sx * 1.38, 0.72, 2.3], 0.3)); // tandem side guard
  }
  // FENDERS over every wheel + rear mudflaps
  const WZ = [-2.7, -0.4, 1.6, 3.0];
  for (const wz of WZ) for (const sx of [-1, 1]) group.add(fenderArc(sx * 1.45, 0.62, wz, 0.7, 0.62, dark));
  for (const sx of [-1, 1]) group.add(tbox(0.56, 0.52, 0.03, dark, [sx * 1.45, 0.42, 3.68], 0.3));
  wheels(WZ.flatMap((wz) => [-1.45, 1.45].map((wx) => ({ x: wx, y: 0.62, z: wz, r: 0.62, w: 0.5, spokes: 6 }))));
  // EXHAUST STACKS behind the cab corners: chrome pipe, perforated heat
  // shield, a bent rain cap
  for (const sx of [-1, 1]) {
    group.add(cyl(0.1, 0.1, 2.9, chrome, [sx * 1.2, 2.45, -1.2], "y", 10));
    group.add(cyl(0.13, 0.13, 0.9, dark, [sx * 1.2, 2.5, -1.2], "y", 12));
    const cap = cyl(0.1, 0.1, 0.24, chrome, [sx * 1.2, 3.96, -1.14], "y", 10);
    cap.rotation.x = 0.6; group.add(cap);
  }
  return {};
}

// ------------------------------------------------------------- SPEEDER ----

/** A ducted hover fan: a lathed duct ring (lip, throat, flared exit) round a
 *  hub and `blades` stator vanes, axis +y, centred at p. */
function ductFan(p: V3, r: number, h: number, mat: THREE.Material, blades = 6): THREE.Mesh {
  const lip = 0.06;
  const ring = new THREE.LatheGeometry([
    new THREE.Vector2(r - lip * 0.3, -h / 2),
    new THREE.Vector2(r + lip * 0.6, -h / 2),
    new THREE.Vector2(r + lip, -h * 0.1),
    new THREE.Vector2(r + lip * 0.75, h / 2),
    new THREE.Vector2(r - lip * 0.2, h / 2 + 0.01),
    new THREE.Vector2(r - lip * 0.5, h * 0.2),
    new THREE.Vector2(r - lip * 0.5, -h * 0.3),
    new THREE.Vector2(r - lip * 0.3, -h / 2),
  ], 22);
  const parts: THREE.BufferGeometry[] = [ring.toNonIndexed()];
  const hub = new THREE.CylinderGeometry(r * 0.22, r * 0.28, h * 0.9, 10);
  parts.push(hub.toNonIndexed());
  for (let i = 0; i < blades; i++) {
    const v = new THREE.BoxGeometry(r * 0.78, h * 0.12, 0.03);
    v.rotateX(0.5); // pitched vane
    v.translate(r * 0.5, h * 0.05, 0);
    v.rotateY((i / blades) * Math.PI * 2);
    parts.push(v.toNonIndexed());
  }
  const geo = mergeGeometries(parts, false)!;
  for (const g of parts) g.dispose();
  ring.dispose(); hub.dispose();
  geo.translate(p[0], p[1], p[2]);
  worldProjectUV(geo, 0.4);
  const m = new THREE.Mesh(geo, mat);
  m.castShadow = true;
  return m;
}

/** NEON SPEEDER hoverbike. Forward −z, ground y 0. Same layout as the old
 *  build: body-paint spine (driver colour / skin), dark seat, accent bars
 *  and fin (driver glow), two glowing hover pads fore and aft. */
export function buildSpeederHull(
  group: THREE.Group, mats: HullMats & { glow: THREE.Material },
): HullParts {
  const { body, accent, dark, glow } = mats;
  const glass = mats.glass ?? dark;
  // BODY: one lofted shell — a sharp nose, a fuel-tank hump ahead of the
  // rider, pinched waist under the seat, a raised tail cowl
  group.add(loft([
    [-1.34, 0.03, 0.03, 0.56],
    [-1.28, 0.12, 0.08, 0.56],
    [-1.1, 0.2, 0.13, 0.57],
    [-0.8, 0.3, 0.17, 0.6],
    [-0.5, 0.36, 0.2, 0.64],
    [-0.2, 0.33, 0.21, 0.66],
    [0.1, 0.24, 0.16, 0.6],
    [0.45, 0.22, 0.14, 0.6],
    [0.75, 0.24, 0.16, 0.66],
    [1.0, 0.18, 0.13, 0.72],
    [1.12, 0.1, 0.08, 0.75],
    [1.16, 0.03, 0.03, 0.76],
  ], body, 16, 2.4, 0.6));
  // keel: a dark belly pan linking the two fans
  group.add(rhull(0.26, 0.12, 1.9, dark, [0, 0.38, 0], 1, 0.4));
  // SEAT: a stitched saddle with a raised pillion step
  group.add(rhull(0.34, 0.12, 0.62, dark, [0, 0.79, 0.3], 1, 0.3));
  group.add(rhull(0.3, 0.1, 0.24, dark, [0, 0.86, 0.66], 1, 0.3));
  // WINDSCREEN + headlight + side intakes
  { const ws = rhull(0.4, 0.22, 0.05, glass, [0, 0.9, -0.66], 1, 0.4); ws.rotation.x = -0.75; group.add(ws); }
  group.add(rbox(0.16, 0.06, 0.05, glass, [0, 0.58, -1.26], 0.2)); // headlight
  for (const sx of [-1, 1]) {
    group.add(rhull(0.08, 0.12, 0.32, dark, [sx * 0.33, 0.6, -0.48], 1, 0.2));  // intake scoop
    for (let i = 0; i < 3; i++) group.add(tbox(0.01, 0.08, 0.02, accent, [sx * 0.375, 0.6, -0.58 + i * 0.09])); // intake vanes
    group.add(tbox(0.02, 0.03, 0.9, accent, [sx * 0.29, 0.63, -0.2]));         // flank light strip
    group.add(rbox(0.16, 0.04, 0.1, dark, [sx * 0.3, 0.5, 0.45], 0.2));        // footpeg
  }
  // HANDLEBARS: clip-on bars on a raked stem, grips, a tiny dash
  group.add(cyl(0.035, 0.04, 0.3, dark, [0, 0.86, -0.82], "y", 8));
  group.add(cyl(0.035, 0.035, 0.62, accent, [0, 0.98, -0.88], "x", 8));
  for (const sx of [-0.31, 0.31]) group.add(cyl(0.045, 0.045, 0.16, dark, [sx, 0.98, -0.88], "x", 8));
  group.add(rbox(0.16, 0.04, 0.1, dark, [0, 0.95, -0.76], 0.2));
  // HOVER FANS fore and aft: ducted shrouds with vanes over the glow pads
  for (const pz of [-0.8, 0.8]) {
    group.add(ductFan([0, 0.27, pz], 0.34, 0.16, dark));
    const pad = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.36, 0.06, 18), glow);
    pad.position.set(0, 0.17, pz); group.add(pad);
  }
  // TAIL: thruster nozzle with a glow core, fin (accent)
  group.add(cyl(0.11, 0.08, 0.26, dark, [0, 0.66, 1.12], "z", 12));
  { const core = cyl(0.065, 0.065, 0.04, glow, [0, 0.66, 1.255], "z", 12); core.castShadow = false; group.add(core); }
  { const fin = rbox(0.05, 0.36, 0.34, accent, [0, 0.9, 0.95], 0.4); fin.rotation.x = 0.45; group.add(fin); }
  return {};
}

// ----------------------------------------------------------- SUBMARINE ----

export interface SubMats extends HullMats {
  /** brass / steel fittings: weld rings, porthole rims, capstan */
  fitting: THREE.Material;
  /** glowing porthole glass (one shared material for every port) */
  port: THREE.Material;
}

/** NAUTILUS submarine. Forward −z, hull axis at y 1.1. Same layout as the old
 *  build (pressure hull + sail + periscope, bow and stern planes, rudder, a
 *  stern screw, glowing portholes) but as one lofted teardrop hull, foil
 *  section sail and control surfaces, and a 7-blade screw. */
export function buildSubmarineHull(group: THREE.Group, mats: SubMats): HullParts {
  const { body: hullMat, accent, dark, fitting, port } = mats;
  const CY = 1.1;
  // PRESSURE HULL: a blunt round bow, a long parallel midbody and a tapering
  // stern cone that ends at the screw shaft (the old capsule was symmetric)
  group.add(loft([
    [-3.32, 0.03, 0.03, CY],
    [-3.26, 0.42, 0.42, CY],
    [-3.08, 0.74, 0.74, CY],
    [-2.75, 0.96, 0.96, CY],
    [-2.2, 1.08, 1.08, CY],
    [-1.6, 1.1, 1.1, CY],
    [1.1, 1.1, 1.1, CY],
    [1.8, 1.0, 1.0, CY],
    [2.4, 0.74, 0.74, CY],
    [2.85, 0.44, 0.44, CY],
    [3.05, 0.24, 0.24, CY],
    [3.08, 0.03, 0.03, CY],
  ], hullMat, 22, 2.0, 1.0));
  // weld-seam rings on the parallel midbody (thin, so they read as seams)
  for (const z of [-1.6, -0.5, 0.6]) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.1, 0.025, 4, 32), fitting);
    ring.position.set(0, CY, z); ring.castShadow = true;
    worldProjectUV(ring.geometry, 0.4); group.add(ring);
  }
  // casing deck: a flat walkway along the top with a hatch and a capstan
  group.add(rbox(0.7, 0.08, 3.6, hullMat, [0, CY + 1.08, -0.3], 0.8));
  group.add(cyl(0.22, 0.22, 0.08, dark, [0, CY + 1.14, 1.0], "y", 14));  // aft hatch
  group.add(cyl(0.12, 0.15, 0.16, fitting, [0, CY + 1.18, -1.8], "y", 10)); // capstan
  // limber holes along the casing edge (dark slots)
  for (const sx of [-1, 1]) for (let i = 0; i < 5; i++) {
    group.add(tbox(0.04, 0.06, 0.24, dark, [sx * 0.36, CY + 1.03, -1.7 + i * 0.6]));
  }
  // SAIL: a foil-section fin with sail planes; periscope + snorkel masts
  {
    const sail = foil([
      { s: 0, le: -0.95, chord: 1.6, t: 0.34, y: 0 },
      { s: 1.0, le: -0.9, chord: 1.4, t: 0.34, y: 0 },
      { s: 1.12, le: -0.86, chord: 1.32, t: 0.3, y: 0 },
    ], hullMat, 8, 0, 0.9);
    sail.geometry.rotateZ(Math.PI / 2);
    sail.geometry.translate(0, CY + 0.75, 0);
    worldProjectUV(sail.geometry, 0.9);
    group.add(sail);
    group.add(foil(mirrored([
      { s: 0, le: -0.75, chord: 0.5, t: 0.12, y: CY + 1.45 },
      { s: 0.75, le: -0.7, chord: 0.38, t: 0.1, y: CY + 1.45 },
    ]), accent, 6, 0, 0.6));
  }
  group.add(cyl(0.05, 0.06, 0.9, dark, [0, CY + 2.0, -0.4], "y", 8));  // periscope
  group.add(rbox(0.5, 0.12, 0.12, accent, [0, CY + 2.45, -0.4], 0.3));   // periscope head
  group.add(cyl(0.07, 0.07, 0.6, dark, [0, CY + 1.95, 0.15], "y", 8));   // snorkel
  // BOW PLANES (accent, through the hull)
  group.add(foil(mirrored([
    { s: 0, le: -2.62, chord: 0.62, t: 0.12, y: CY - 0.05 },
    { s: 1.2, le: -2.56, chord: 0.46, t: 0.1, y: CY - 0.05 },
  ]), accent, 6, 0, 0.6));
  // STERN: cruciform planes + rudder (accent), end-plates on the planes
  group.add(foil(mirrored([
    { s: 0, le: 1.78, chord: 0.9, t: 0.12, y: CY },
    { s: 1.68, le: 2.0, chord: 0.62, t: 0.1, y: CY },
  ]), accent, 6, 0, 0.6));
  for (const sx of [-1, 1]) group.add(rbox(0.06, 0.4, 0.6, accent, [sx * 1.68, CY, 2.3], 0.3));
  {
    const rudder = foil([
      { s: -1.0, le: 1.95, chord: 0.72, t: 0.12, y: 0 },
      { s: 0, le: 1.78, chord: 0.9, t: 0.12, y: 0 },
      { s: 1.25, le: 2.0, chord: 0.62, t: 0.1, y: 0 },
    ], accent, 6, 0, 0.6);
    rudder.geometry.rotateZ(Math.PI / 2);
    rudder.geometry.translate(0, CY, 0);
    worldProjectUV(rudder.geometry, 0.6);
    group.add(rudder);
  }
  // PORTHOLES: one glow material for all six, brass rims
  for (const sx of [-1, 1]) for (const pz of [-1.5, -0.4, 0.7]) {
    const disc = new THREE.Mesh(new THREE.CircleGeometry(0.15, 12), port);
    disc.position.set(sx * 1.105, CY + 0.1, pz);
    disc.rotation.y = sx > 0 ? Math.PI / 2 : -Math.PI / 2;
    group.add(disc);
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.03, 5, 14), fitting);
    rim.position.copy(disc.position); rim.rotation.y = Math.PI / 2; rim.castShadow = true;
    worldProjectUV(rim.geometry, 0.3); group.add(rim);
  }
  // SCREW: 7 twisted blades round a hub cone (spins about +z)
  const rotor = new THREE.Group();
  rotor.position.set(0, CY, 3.12);
  rotor.add(cyl(0.2, 0.06, 0.34, dark, [0, 0, 0.05], "z", 12)); // hub
  for (let i = 0; i < 7; i++) {
    const blade = foil([
      { s: 0.12, le: -0.12, chord: 0.22, t: 0.14, y: 0, tw: 0.9 },
      { s: 0.42, le: -0.1, chord: 0.3, t: 0.1, y: 0, tw: 1.05 },
      { s: 0.62, le: -0.02, chord: 0.18, t: 0.08, y: 0, tw: 1.2 },
    ], dark, 5, 0.03, 0.3);
    blade.geometry.rotateZ(Math.PI / 2 + (i / 7) * Math.PI * 2);
    worldProjectUV(blade.geometry, 0.3);
    rotor.add(blade);
  }
  group.add(rotor);
  return { rotor };
}
